import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef } from 'react'
import { toast } from 'sonner'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, Check, Download, ExternalLink, Send } from 'lucide-react'
import { LoadingButton } from '@/components/loading-button'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { StatusPill } from '@/components/status-pill'
import { Button } from '@/components/ui/button'
import { cn, formatMoney } from '@/lib/utils'
import { newRequestId } from '@/features/quote-builder/publishing/api'
import { RefundDialog } from './refund-dialog'
import { invoiceDocumentHref } from '@/features/quote-builder/invoicing/api'
import { QuoteConversations } from '@/features/quote-builder/emails/quote-conversations'
import { ErrorState, LoadingState, SurfaceCard } from '@/features/quote-builder/ui/page'
import {
  formatDay,
  formatWhen,
  newInvoiceErrorMessage,
  receivableQueryOptions,
  receivablesApi,
  receivablesKeys,
  receivableState,
  useReceivablesApi,
  type ReceivableDetail,
} from './api'
import { usePageTitle } from '@/lib/page-title'

const REMINDER_CHOICES = [
  { value: 'on', label: 'On' },
  { value: 'paused', label: 'Paused' },
] as const

const PAYMENT_LABELS: Record<string, string> = {
  payment: 'Payment received',
  payment_out_of_band: 'Marked paid',
  payment_processing: 'Bank payment processing',
  payment_failed: 'Payment failed',
  refund: 'Refunded',
  credit: 'Taken off',
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium tabular-nums">{children}</dd>
    </div>
  )
}

function Reminders({ invoice }: { invoice: ReceivableDetail }) {
  const api = useReceivablesApi()
  const client = useQueryClient()
  const applied = (next: ReceivableDetail) => {
    client.setQueryData(receivablesKeys.detail(invoice.invoice_id), next)
    void client.invalidateQueries({ queryKey: ['receivables', 'list'] })
  }
  const pause = useMutation({
    mutationFn: (paused: boolean) =>
      api<ReceivableDetail>(`/${invoice.invoice_id}/reminders`, {
        method: 'PATCH',
        body: JSON.stringify({ reminders_paused: paused }),
      }),
    onSuccess: applied,
  })
  const sendNow = useMutation({
    mutationFn: () =>
      api<ReceivableDetail>(`/${invoice.invoice_id}/reminders/send`, {
        method: 'POST',
      }),
    onSuccess: applied,
  })
  const collecting = invoice.status === 'open' || invoice.status === 'overdue'
  if (!invoice.reminders.length) return null

  return (
    <SurfaceCard title="Payment reminders">
      <div className="grid gap-3 p-4">
        {collecting ? (
          <ReviewChoiceGroup
            disabled={pause.isPending}
            label="Automatic reminders"
            onChange={(value) => pause.mutate(value === 'paused')}
            options={REMINDER_CHOICES}
            value={invoice.reminders_paused ? 'paused' : 'on'}
          />
        ) : null}
        <ol className="grid gap-1 text-sm">
          {invoice.reminders.map((step) => (
            <li
              className={cn(
                'flex items-center justify-between gap-3',
                !step.sent_at && (invoice.reminders_paused || !collecting) && 'text-muted-foreground',
              )}
              key={step.step}
            >
              <span className="flex items-center gap-2">
                {step.sent_at ? (
                  <Check aria-label="Sent" className="size-3.5 text-emerald-600" />
                ) : (
                  <span aria-hidden="true" className="size-3.5" />
                )}
                {step.label}
              </span>
              <span className="tabular-nums text-muted-foreground">
                {step.sent_at ? formatWhen(step.sent_at) : formatDay(step.scheduled_on)}
              </span>
            </li>
          ))}
        </ol>
        {collecting ? (
          <LoadingButton
            loading={sendNow.isPending}
            loadingText="Sending..."
            onClick={() => sendNow.mutate()}
            size="sm"
            variant="outline"
          >
            <Send aria-hidden="true" />
            Send reminder now
          </LoadingButton>
        ) : null}
        {pause.error || sendNow.error ? (
          <p className="text-xs text-destructive" role="alert">
            {sendNow.error ? 'The reminder did not send. Try again.' : 'That did not save. Try again.'}
          </p>
        ) : null}
      </div>
    </SurfaceCard>
  )
}

/** What an additional invoice bills, line by line (USA-378). */
function Items({ invoice }: { invoice: ReceivableDetail }) {
  return (
    <SurfaceCard title="Items">
      <ul className="divide-y">
        {invoice.items.map((item, index) => (
          <li className="flex items-baseline justify-between gap-3 px-4 py-2.5 text-sm" key={index}>
            <span className="min-w-0">
              <span className="block break-words">{item.description}</span>
              {item.quantity && Number(item.quantity) !== 1 ? (
                <span className="text-xs tabular-nums text-muted-foreground">
                  {item.quantity} × {formatMoney(Number(item.full_amount) / Number(item.quantity))}
                </span>
              ) : null}
            </span>
            <span className="shrink-0 font-medium tabular-nums">{formatMoney(item.amount)}</span>
          </li>
        ))}
      </ul>
      {invoice.memo ? <p className="whitespace-pre-line border-t px-4 py-3 text-sm text-muted-foreground">{invoice.memo}</p> : null}
    </SurfaceCard>
  )
}

/** Email an additional invoice from your mailbox, or email it again. */
function SendInvoice({ invoice }: { invoice: ReceivableDetail }) {
  const api = useReceivablesApi()
  const client = useQueryClient()
  const requestId = useRef<string | null>(null)
  const send = useMutation({
    retry: false,
    mutationFn: () => receivablesApi.send(api, invoice.invoice_id, (requestId.current ??= newRequestId())),
    onSuccess: (next) => {
      requestId.current = null
      client.setQueryData(receivablesKeys.detail(invoice.invoice_id), next)
      void client.invalidateQueries({ queryKey: ['receivables', 'list'] })
      toast.success(`Invoice ${next.invoice_number} sent to ${next.recipient_email}`)
    },
  })
  return (
    <div className="mt-3 grid gap-2">
      <LoadingButton loading={send.isPending} loadingText="Sending..." onClick={() => send.mutate()} size="sm" variant={invoice.sent_at ? 'outline' : 'default'}>
        <Send aria-hidden="true" />
        {invoice.sent_at ? 'Resend invoice' : 'Send invoice'}
      </LoadingButton>
      {send.isError ? (
        <p className="text-xs text-destructive" role="alert">
          {newInvoiceErrorMessage(send.error)}
        </p>
      ) : null}
    </div>
  )
}

/** One customer invoice: balance, payments, reminders and the email thread (USA-1094). */
export function ReceivableDetailPage({ invoiceId }: { invoiceId: string }) {
  const api = useReceivablesApi()
  const query = useQuery(receivableQueryOptions(api, invoiceId))
  usePageTitle(query.data && `Invoice ${query.data.invoice_number}`)

  if (query.isLoading) return <LoadingState label="Loading invoice" />
  if (!query.data) return <ErrorState onRetry={() => void query.refetch()} title="Invoice unavailable" />
  const invoice = query.data
  const state = receivableState(invoice)
  const payments = invoice.ledger.filter((entry) => entry.entry_type in PAYMENT_LABELS)

  return (
    <div className="mx-auto w-full max-w-6xl pb-24">
      <Button asChild className="-ml-2 mb-2" size="sm" variant="ghost">
        <Link to="/receivables">
          <ArrowLeft aria-hidden="true" />
          AR Invoices
        </Link>
      </Button>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight">Invoice {invoice.invoice_number}</h1>
          <p className="mt-1 flex flex-wrap gap-x-2 text-sm text-muted-foreground">
            {invoice.customer_group_uuid ? (
              <Link
                className="font-medium text-foreground hover:underline"
                params={{ groupId: invoice.customer_group_uuid }}
                to="/customers/$groupId"
              >
                {invoice.customer_name}
              </Link>
            ) : (
              <span className="font-medium text-foreground">{invoice.customer_name}</span>
            )}
            {invoice.quote_id ? (
              <>
                <span aria-hidden="true">·</span>
                <Link
                  className="hover:underline"
                  params={{ quoteId: invoice.quote_id, stage: 'invoicing' }}
                  to="/quotes/$quoteId/workflow/$stage"
                >
                  {invoice.quote_number} {invoice.quote_title}
                </Link>
              </>
            ) : null}
            {invoice.project_uuid ? (
              <>
                <span aria-hidden="true">·</span>
                <Link
                  className="hover:underline"
                  params={{ projectId: invoice.project_uuid }}
                  to="/projects/$projectId"
                >
                  {invoice.quote_id ? 'Project' : (invoice.project_name ?? 'Project')}
                </Link>
              </>
            ) : null}
            {invoice.salesperson_name ? (
              <>
                <span aria-hidden="true">·</span>
                <span>{invoice.salesperson_name}</span>
              </>
            ) : null}
          </p>
        </div>
        <StatusPill className="text-xs" tone={state.tone}>
          {state.label}
        </StatusPill>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="order-2 grid content-start gap-4 lg:order-1">
          {invoice.kind === 'additional' ? <Items invoice={invoice} /> : null}
          <QuoteConversations invoiceId={invoice.invoice_id} quoteId={invoice.quote_id ?? null} title="Emails" />
        </div>

        <div className="order-1 grid content-start gap-4 lg:order-2">
          <SurfaceCard>
            <div className="p-4">
              <p className="text-xs font-medium text-muted-foreground">Balance</p>
              <p className="text-3xl font-bold tabular-nums">{formatMoney(invoice.amount_remaining)}</p>
              <dl className="mt-3 divide-y border-t">
                <Fact label={invoice.invoice_label}>{formatMoney(invoice.amount_due)}</Fact>
                <Fact label="Paid">{formatMoney(invoice.amount_paid)}</Fact>
                <Fact label="Due">{formatDay(invoice.due_at)}</Fact>
                <Fact label="Emailed">{formatWhen(invoice.sent_at)}</Fact>
                {invoice.paid_at ? <Fact label="Paid on">{formatWhen(invoice.paid_at)}</Fact> : null}
                {invoice.created_by_name ? <Fact label="Created by">{invoice.created_by_name}</Fact> : null}
              </dl>
              {invoice.kind === 'additional' && invoice.status !== 'paid' && invoice.status !== 'void' ? (
                <SendInvoice invoice={invoice} />
              ) : null}
              {invoice.status === 'paid' || invoice.status === 'open' || invoice.status === 'overdue' ? (
                <div className="mt-2 grid">
                  <RefundDialog source={{ invoiceId: invoice.invoice_id }} />
                </div>
              ) : null}
              <div className="mt-3 flex flex-wrap gap-2">
                {invoice.pay_url ? (
                  <Button asChild className="flex-1" size="sm" variant="outline">
                    {/* eslint-disable-next-line no-restricted-syntax -- the customer's invoice page */}
                    <a href={invoice.pay_url} rel="noopener noreferrer" target="_blank">
                      <ExternalLink aria-hidden="true" />
                      View as customer
                    </a>
                  </Button>
                ) : null}
                {invoice.pdf_url ? (
                  <Button asChild className="flex-1" size="sm" variant="outline">
                    {/* eslint-disable-next-line no-restricted-syntax -- the invoice PDF */}
                    <a href={invoiceDocumentHref(invoice.pdf_url)} rel="noopener noreferrer" target="_blank">
                      <Download aria-hidden="true" />
                      PDF
                    </a>
                  </Button>
                ) : null}
              </div>
            </div>
          </SurfaceCard>

          <Reminders invoice={invoice} />

          <SurfaceCard title="Payments">
            {payments.length ? (
              <ul className="divide-y">
                {payments.map((entry) => (
                  <li className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm" key={entry.id}>
                    <span className="min-w-0">
                      <span className="block font-medium">{PAYMENT_LABELS[entry.entry_type]}</span>
                      <span className="block text-xs text-muted-foreground">
                        {formatWhen(entry.occurred_at)}
                        {entry.note ? ` · ${entry.note}` : ''}
                      </span>
                    </span>
                    <span className="shrink-0 whitespace-nowrap font-semibold tabular-nums">
                      {entry.entry_type === 'refund' || entry.entry_type === 'credit'
                        ? `−${formatMoney(entry.amount)}`
                        : formatMoney(entry.amount)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-4 py-3 text-sm text-muted-foreground">No payments yet</p>
            )}
            {invoice.credits.length ? (
              <ul className="divide-y border-t">
                {invoice.credits.map((credit) => (
                  <li className="px-4 py-2.5 text-sm" key={credit.id}>
                    {credit.credit_note_pdf_url ? (
                      // eslint-disable-next-line no-restricted-syntax -- the Stripe credit note PDF
                      <a
                        className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
                        href={credit.credit_note_pdf_url}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        Credit note {credit.stripe_credit_note_number}
                        <Download aria-hidden="true" className="size-3.5" />
                      </a>
                    ) : (
                      <span className="font-medium">Credit note {credit.stripe_credit_note_number}</span>
                    )}
                    <ul className="mt-1 grid gap-0.5 text-xs text-muted-foreground">
                      {credit.items.map((item, index) => (
                        <li className="flex justify-between gap-3" key={index}>
                          <span className="min-w-0 truncate">{item.description}</span>
                          <span className="shrink-0 tabular-nums">
                            {formatMoney(Number(item.quantity) * Number(item.unit_price))}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            ) : null}
          </SurfaceCard>
        </div>
      </div>
    </div>
  )
}
